import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CompanyBasic, Note } from '../../models';
import { AuthorizationService } from 'src/app/core/services';
import { PERMISSIONS } from '../../enums/permissions.enum';
import { PortfolioColors } from '../../constants/portfolio-colors';
import { StockType } from '../../enums/stock-type.enum';
import { CompanyFinancialData } from '../../models/company/company-financial-data.interface';
import { CompanyNotesService, CompanyService } from '../../../core/data-services/company';
import { Subject } from 'rxjs';
import { CrmNoteService } from '../../../core/data-services/crm-note.service';
import { finalize } from 'rxjs/operators';
import { CONFIG } from '../../../config';

@Component({
    selector: 'rms-company-tooltip-data',
    templateUrl: './company-tooltip-data.component.html',
    styleUrls: ['./company-tooltip-data.component.scss'],
    standalone: false
})
export class CompanyTooltipDataComponent implements OnInit, OnChanges {

  @Input() companyId: number;
  @Input() companyFinancial: CompanyFinancialData;
  @Input() percentFund: number;
  public company: CompanyBasic;
  public showPriceTarget = false;
  public hasPermission: Record<string, boolean> = {};
  public totalPositionsValue = 0;
  public isFullyBoxedMode = false;
  public latestNote: Note = null;
  public noteLoading = false;
  public readonly PORTFOLIO_COLORS = PortfolioColors;
  public readonly STOCK = StockType.STOCK;
  public readonly Math: Math = Math;
  public formatEST = CONFIG.formatEST;
  public shortDateFormat = CONFIG.shortDateFormat;
  public chartOptions: any = {
    series: [
      {
        name: 'Portfolios',
        type: 'pie',
        height: '100px',
        silent: true,
        radius: ['65%', '80%'],
        center: ['40%', '40%'],
        label: { show: false },
        data: [],
        padAngle: 3
      }
    ]
  };
  private $companyLoad: Subject<boolean> = new Subject<boolean>();

  constructor(
    public crmNoteService: CrmNoteService,
    private authorizationService: AuthorizationService,
    private companyService: CompanyService,
    private companyNotesService: CompanyNotesService,
  ) {
    this.hasPermission = {
      priceTargetRead: this.authorizationService.hasPermission([PERMISSIONS.CorePriceTargetRead]),
      corePortfolioRead: this.authorizationService.hasPermission([PERMISSIONS.CorePortfolioRead]),
    };
  }

  ngOnInit() {
    this.fetchCompany();
    this.$companyLoad.subscribe((resp) => {
      if (resp) {
        this.totalPositionsValue = this.getTotalPositionValue();
        this.isFullyBoxedMode = this.computeIsFullyBoxedMode();
        this.fetchLatestNote();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.companyFinancial.currentValue) {
      const cf = changes.companyFinancial.currentValue;
      this.showPriceTarget = this.hasPermission.priceTargetRead && (!!cf.longPrice || !!cf.fairPrice || !!cf.shortPrice);
    }
  }

  public onChartInit(chartInstance: any) {
    if (this.company) {
      this.setChartData(chartInstance);
    } else {
      this.$companyLoad.subscribe((resp) => {
        if (resp) {
          this.setChartData(chartInstance);
        }
      });
    }
  }

  public getPortfolioValue(portfolioId: any) {
    if (!this.company || !this.company.aggregatePosition || !this.company.aggregatePosition.fundValues) {
      return 0;
    }

    return this.company.aggregatePosition.fundValues[portfolioId].reduce((acc, pos) => acc + pos.portfolioValue, 0);
  }

  /**
   * Whether any of the portfolio's fund value entries comes from a fully boxed position
   * (boxed with net zero shares).
   */
  public isPortfolioFullyBoxed(portfolioId: string | number): boolean {
    return !!this.company?.aggregatePosition?.fundValues?.[portfolioId]?.some(v => v.fullyBoxed === true);
  }

  public getStockType(stockType: string): string {
      return stockType === StockType.CONVERTIBLE_NOTE ? 'Conv. Note' : stockType;
  }

  private fetchCompany(): void {
    const params = { 'extrafield': ['aggregatePosition', 'financialFields', 'companyContentRoots', 'crmItems'] };
    this.companyService.getCompany(this.companyId, params).subscribe(resp => {
      this.company = new CompanyBasic(resp);
      this.$companyLoad.next(true);
    });
  }

  private getTotalPositionValue(): number {
    if (!this.company.aggregatePosition || !this.company.aggregatePosition.fundValues) {
      return 0;
    }
    let val = 0;
    for (const positions of Object.values(this.company.aggregatePosition.fundValues)) {
      positions.forEach(p => val += p.portfolioValue);
    }
    return val;
  }

  /**
   * The company is shown in fully-boxed mode when at least one fund is fully boxed and the
   * aggregate holding nets to zero shares, so cost, share count, P/L and the chart are meaningless.
   */
  private computeIsFullyBoxedMode(): boolean {
    const aggregatePosition = this.company.aggregatePosition;
    if (!aggregatePosition?.fundValues || aggregatePosition.shares) {
      return false;
    }
    return Object.keys(aggregatePosition.fundValues).some(id => this.isPortfolioFullyBoxed(id));
  }

  private setChartData(chartInstance: any) {
    const data = [];
    const minPercent = 0.02;
    const totalVal = this.getTotalPositionValue();
    const minValue = totalVal * minPercent / (1 - minPercent);

    for (const portfolioId of Object.keys(this.company.aggregatePosition.fundValues)) {
      this.company.aggregatePosition.fundValues[portfolioId].forEach(position => {
        data.push({
          name: position.portfolioName,
          value: Math.max(position.portfolioValue, minValue),
          itemStyle: {color: PortfolioColors[portfolioId]}
        });
      });
    }
    this.chartOptions.series[0].data = data;
    chartInstance.setOption(this.chartOptions);
  }

  private fetchLatestNote(): void {
    this.noteLoading = true;
    const params: any = {
      'spaceId': String(this.company.defaultContentRootId) || '',
      'pagesize': '1',
      'direction': 'DESC',
      'sort': ['timestamp']
    };
    if (this.company.activeCrmItemIds && this.company.activeCrmItemIds.length > 0) {
      params.crmId = this.company.activeCrmItemIds;
    }
    this.companyNotesService.getSpaceNotes(params)
      .pipe(finalize(() => this.noteLoading = false))
      .subscribe((resp) => {
      if (resp && resp.records && resp.records.length > 0) {
        this.latestNote = new Note(resp.records[0]);
      }
    });
  }
}
